import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Pencil, Rocket, CalendarClock, FileText } from "lucide-react";

const STATUS_DOT = {
  draft: "bg-muted-foreground",
  scheduled: "bg-amber-500",
  published: "bg-emerald-500",
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function DraftDialog({ t, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [title, setTitle] = useState(initial.title || "");
  const [body, setBody] = useState(initial.body || "");
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(initial.scheduled_at));
  const [innerCircle, setInnerCircle] = useState(Boolean(initial.inner_circle));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (title.trim().length < 1) { toast.error(t("ps.err_title")); return; }
    setSaving(true);
    const sched = scheduledAt ? new Date(scheduledAt).toISOString() : "";
    const payload = { title: title.trim(), body: body.trim(), inner_circle: innerCircle, scheduled_at: sched || (isEdit ? "" : null) };
    try {
      if (isEdit) await api.patch(`/coach/post-scheduler/drafts/${initial.id}`, payload);
      else await api.post("/coach/post-scheduler/drafts", { title: title.trim(), body: body.trim(), inner_circle: innerCircle, scheduled_at: sched || null });
      toast.success(t("ps.saved"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const publishNow = async () => {
    if (!isEdit) return;
    setSaving(true);
    try { await api.post(`/coach/post-scheduler/drafts/${initial.id}/publish`); toast.success(t("ps.published")); onSaved(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="ps-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("ps.dialog_edit") : t("ps.dialog_new")}</DialogTitle>
          <DialogDescription>{t("ps.dialog_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ps.f_title")}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("ps.f_title_ph")} data-testid="ps-title" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ps.f_body")}</label>
            <Textarea className="min-h-[140px]" value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("ps.f_body_ph")} data-testid="ps-body" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ps.f_scheduled")}</label>
            <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="ps-scheduled" />
            <p className="mt-1 text-xs text-muted-foreground">{t("ps.f_scheduled_hint")}</p>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
            <p className="text-sm font-medium text-foreground">{t("ps.f_inner_circle")}</p>
            <Switch checked={innerCircle} onCheckedChange={setInnerCircle} data-testid="ps-inner-circle" />
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {isEdit && <Button variant="outline" className="gap-1.5 rounded-full" onClick={publishNow} disabled={saving} data-testid="ps-publish"><Rocket className="h-4 w-4" /> {t("ps.publish_now")}</Button>}
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="ps-cancel">{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="ps-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PostSchedulerTab() {
  const t = useT();
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try { const { data: d } = await api.get(`/coach/post-scheduler/month?year=${ym.year}&month=${ym.month}`); setData(d); }
    catch { setData({ days: [], undated_drafts: [], totals: {} }); }
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  const monthLabel = useMemo(() => {
    try { return new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(new Date(ym.year, ym.month - 1, 1)); }
    catch { return `${ym.month}/${ym.year}`; }
  }, [ym]);

  const shift = (delta) => setYm((p) => {
    let m = p.month + delta, y = p.year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    return { year: y, month: m };
  });

  const firstWeekday = useMemo(() => {
    const wd = new Date(ym.year, ym.month - 1, 1).getDay(); // 0=Sun
    return (wd + 6) % 7; // Mon=0
  }, [ym]);

  const totals = data?.totals || {};
  const onEdit = (post) => { if (post.status === "published") return; setEditing(post); };

  return (
    <div data-testid="ps-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => shift(-1)} data-testid="ps-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <p className="min-w-[160px] text-center font-display text-lg text-foreground" data-testid="ps-month-label">{monthLabel}</p>
          <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => shift(1)} data-testid="ps-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <Button className="gap-1.5 rounded-full" onClick={() => setEditing({})} data-testid="ps-new"><Plus className="h-4 w-4" /> {t("ps.new")}</Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT.draft}`} /> {t("ps.status_draft")}: <strong className="text-foreground">{totals.drafts ?? 0}</strong></span>
        <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT.scheduled}`} /> {t("ps.status_scheduled")}: <strong className="text-foreground">{totals.scheduled ?? 0}</strong></span>
        <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT.published}`} /> {t("ps.status_published")}: <strong className="text-foreground">{totals.published ?? 0}</strong></span>
      </div>

      {data?.next_scheduled_at && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="ps-next-hint">
          <CalendarClock className="h-4 w-4 shrink-0" /> {t("ps.next_hint", { title: data.next_scheduled_title || "", when: shortDate(data.next_scheduled_at) })}
        </div>
      )}

      {data === null ? (
        <Skeleton className="h-80 rounded-2xl" />
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="grid grid-cols-7 border-b border-border bg-secondary/40 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {[t("ps.wd_mon"), t("ps.wd_tue"), t("ps.wd_wed"), t("ps.wd_thu"), t("ps.wd_fri"), t("ps.wd_sat"), t("ps.wd_sun")].map((d, i) => (
                <div key={i} className="py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: firstWeekday }).map((_, i) => <div key={`pad-${i}`} className="min-h-[84px] border-b border-r border-border/50 bg-secondary/10" />)}
              {(data.days || []).map((day) => {
                const dayNum = parseInt(day.date.slice(8, 10), 10);
                return (
                  <div key={day.date} className="min-h-[84px] border-b border-r border-border/50 p-1.5" data-testid={`ps-day-${day.date}`}>
                    <p className="mb-1 text-[11px] font-semibold text-muted-foreground">{dayNum}</p>
                    <div className="space-y-1">
                      {(day.posts || []).slice(0, 3).map((p) => (
                        <button key={p.id} onClick={() => onEdit(p)} data-testid={`ps-post-${p.id}`}
                          className="flex w-full items-center gap-1 rounded-md bg-secondary px-1.5 py-1 text-left text-[11px] text-foreground hover:bg-accent">
                          <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[p.status] || STATUS_DOT.draft}`} />
                          <span className="min-w-0 flex-1 truncate">{p.title}</span>
                        </button>
                      ))}
                      {(day.posts || []).length > 3 && <p className="text-[10px] text-muted-foreground">+{day.posts.length - 3}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Undated drafts */}
          <div className="mt-5">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground"><FileText className="h-4 w-4 text-primary" /> {t("ps.undated_title")}</p>
            {(data.undated_drafts || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("ps.undated_empty")}</p>
            ) : (
              <div className="space-y-2">
                {data.undated_drafts.map((p) => (
                  <div key={p.id} data-testid={`ps-draft-${p.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[p.status] || STATUS_DOT.draft}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">{p.title}</p>
                      {p.body && <p className="truncate text-xs text-muted-foreground">{p.body}</p>}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" onClick={() => setEditing(p)} data-testid={`ps-draft-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {editing && (
        <DraftDialog
          t={t}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}
